import Loader from '@cpg-its/ads.common.loading-circle';
import Tooltip from '@cpg-its/ads.common.tooltip';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { NewHireProps } from '../../../models/arc-epi-props';
import { LabelAndValue } from '../../../../../global/components/data-display/label-value';
import { EmploymentCompensationState } from '../../employment-compensation/employment-compensation-models';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { labelSupplementStyles } from '../../../../../global/constants/theme/styles/components/label-supplement-styles';
import { tcsmColors } from '../../../../../global/constants/theme/theme-constants/colors';
import { EmploymentState } from '../../employment/employment-model';
import { ButtonWrapper } from '../../../../../../ECDApp/global/components/layout/button-wrapper';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../new-hire-enrollment/slice/new-hire-enrollment-slices';
import { BenefitEnrollmentStep } from '../../new-hire-enrollment/models/pension-enrollment-models';

interface Props {	
	newHireProps: NewHireProps;
	dioApproverNavigationLinks?: {
		employmentPrevStep: string,
		employmentNextStep: string,
	};
}
export function NewHireStipEmploymentCompensationReview(props: Props) {
	const { newHireProps, dioApproverNavigationLinks } = props;
	
	const history = useHistory();
	const dispatch = useDispatch();		
	const labelClasses = labelSupplementStyles();	
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state?.employmentState);
	const employmentCompState = useSelector<TcsmStates, EmploymentCompensationState>(state => state?.employmentCompensationState);
	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const currentEmployment = employmentState?.currentEmployment;
	const employmentCompensationOneTimePayment = employmentCompState?.employmentCompensation[0]?.compensations?.otp[0];
	const employmentCompensationScheduledPayment = employmentCompState?.employmentCompensation[0]?.compensations?.compensations[0];

	function RenderScheduledPaymentFields(){
		if (!employmentCompensationScheduledPayment) return null;

		return (
			<>				
				<div className={`${labelClasses.labelLayout} divider-section-item-header`}>
					<h4>{newHireProps?.scheduledPaymentHeader}</h4>
					<div className='supplement-wrapper'>
						<Tooltip iconColor={tcsmColors.blue} title={newHireProps?.scheduledPaymentTooltip} />
					</div>
				</div>
				<LabelAndValue label={newHireProps?.compEffectiveDateLabel} value={getFormattedDate(employmentCompensationScheduledPayment?.effective_date)} />
				<h4 className='divider-section-item-header'>{newHireProps?.annualIncomeAmountsHeader}</h4>
				<LabelAndValue label={newHireProps?.baseSalaryLabel} value={employmentCompensationScheduledPayment?.cash_salary} 
					tooltipMessage={newHireProps?.baseSalaryTooltip} />
				<LabelAndValue label={newHireProps?.employerContributionsLabel} value={employmentCompensationScheduledPayment?.er_plan_contributions}
					tooltipMessage={newHireProps?.employerContributionsTooltip} />
				<LabelAndValue label={newHireProps?.cashHousingAllowanceLabel} value={employmentCompensationScheduledPayment?.housing_cash_allowance}
					tooltipMessage={newHireProps?.cashHousingAllowanceTooltip} />
				<h4 className='divider-section-item-header'>{newHireProps?.housingHeader}</h4>
				<LabelAndValue label={newHireProps?.employerProvidedHousingLabel} 
					value={employmentCompensationScheduledPayment?.housing_provided?.toLowerCase() === 'housing' ? (labelSetting?.yesText || 'Yes') : (labelSetting?.noText || 'No') }
					tooltipMessage={newHireProps?.employerProvidedHousingTooltip} />			
			</>
		);
	}

	function RenderOneTimePaymentFields(){
		if (!employmentCompensationOneTimePayment) return null;
		return (
			<>
				<div className={`${labelClasses.labelLayout} divider-section-item-header`}>
					<h4>{newHireProps?.oneTimePaymentHeader}</h4>
					<div className='supplement-wrapper'>
						<Tooltip iconColor={tcsmColors.blue} title={newHireProps?.oneTimePaymentTooltip} />
					</div>
				</div>
				<LabelAndValue label={newHireProps?.compEffectiveDateLabel} value={employmentCompensationOneTimePayment?.effective_date} />
				<LabelAndValue label={newHireProps?.oneTimePaymentAmountLabel} value={employmentCompensationOneTimePayment?.amount} />				
			</>
		);
	}	
	
	function RenderPosition(){
		return (
			<>
				<LabelAndValue label={newHireProps?.positionTitleLabel} value={currentEmployment?.current_position_title}
					tooltipMessage={newHireProps?.positionTitleTooltip} />
				<LabelAndValue label={newHireProps?.positionEffectiveDateLabel} value={getFormattedDate(currentEmployment?.current_position_title_start_date)} />
				<LabelAndValue label={newHireProps?.publishedTitleLabel} value={currentEmployment?.position_titles[0]?.published_title}
					tooltipMessage={newHireProps?.publishedTitleTooltip} />
				<LabelAndValue label={newHireProps?.hoursPerYearLabel} value={currentEmployment?.current_scheduled_hours} />
				<LabelAndValue label={newHireProps?.scheduledHoursEffectiveDateLabel} value={getFormattedDate(currentEmployment?.current_scheduled_hours_start_date)} />
			</>
		);
	}

	const handleContinueButton = () => {
		dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.COVERAGE));
		history.push(dioApproverNavigationLinks.employmentNextStep);
	};

	const handleBackButton = () => {
		dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.RELATIONSHIP));
		history.push(dioApproverNavigationLinks.employmentPrevStep);
	};

	const hadleRenderEmploymentButtons = () => {

		const classes = buttonStyles();
		return (
			<ButtonWrapper flexEnd>
				<button type='button' className={classes.tertiaryButton} onClick={()=> handleBackButton()}>{newHireProps?.backToRelationshipButtonText}</button>
				<button type='button' className={classes.primaryButton} onClick={() => handleContinueButton()} >{newHireProps?.continueToCoverageButtonText}</button>
			</ButtonWrapper>
		);
	};

	if (!employmentState?.employmentLoaded || !employmentCompState?.employmentCompensationLoaded) {
		return (<Loader />);
	}

	return (
		<>
			<DividerSection sectionText={newHireProps?.employmentHeader}>
				<LabelAndValue label={newHireProps?.employmentHireDateLabel} value={getFormattedDate(currentEmployment?.hire_date)}
					tooltipMessage={newHireProps?.employmentHireDateTooltip} />
				<RenderPosition />								
			</DividerSection>
			<DividerSection sectionText={newHireProps?.compensationHeader} removeDivider>				
				<RenderScheduledPaymentFields/>
				<RenderOneTimePaymentFields/>
				{
					dioApproverNavigationLinks && (hadleRenderEmploymentButtons())
				}
			</DividerSection>
		</>
	);
}


NewHireStipEmploymentCompensationReview.defaultProps = {
    dioApproverNavigationLinks: null,
};